import React from 'react';
import { Link } from 'react-router-dom';
import TudoConversorLogo from './TudoConversorLogo.jsx';
import AdSense from '@/components/AdSense.jsx';
import ShareButtons from '@/components/ShareButtons.jsx';
import { useI18n } from '@/i18n/index.jsx';

function Footer() {
  const { lang } = useI18n();
  const isEN = lang === 'en';

  // Localize every internal path so EN pages link to EN routes
  // (matches the Header pattern) — prevents link-juice leakage and
  // hreflang breakage on English pages.
  const getLocalizedPath = (path) => {
    const normalized = path.endsWith('/') ? path : `${path}/`;
    return isEN ? `/en${normalized === '/' ? '' : normalized}` : normalized;
  };

  return (
    <footer className="bg-primary text-primary-foreground mt-auto" aria-label={isEN ? 'Site footer' : 'Rodapé do site'}>
      
      <div className="bg-background w-full border-t border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AdSense adFormat="horizontal" className="my-0" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-8">
          
          <div className="space-y-4 md:col-span-1">
            <Link to={getLocalizedPath('/')} className="inline-block hover:opacity-90 transition-opacity" aria-label={isEN ? 'Back to home' : 'Voltar ao início'}>
              <TudoConversorLogo />
            </Link>
            <p className="text-primary-foreground/80 text-sm leading-relaxed max-w-sm mt-4">
              {isEN ? 'The ultimate conversion platform. Accurate, fast, and free calculations for engineering, science, and everyday use.' : 'A plataforma definitiva de conversão. Cálculos precisos, rápidos e gratuitos para engenharia, ciência e seu uso diário.'}
            </p>
          </div>

          <div>
            <p className="text-lg font-semibold mb-4 text-white">{isEN ? 'Tools & Categories' : 'Ferramentas & Categorias'}</p>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><Link to={getLocalizedPath('/categoria/common/')} className="hover:text-white transition-colors">{isEN ? 'Common Converters' : 'Conversores Comuns'}</Link></li>
              <li><Link to={getLocalizedPath('/categoria/engineering/')} className="hover:text-white transition-colors">{isEN ? 'Engineering & Physics' : 'Engenharia e Física'}</Link></li>
              <li><Link to={getLocalizedPath('/categoria/electricity/')} className="hover:text-white transition-colors">{isEN ? 'Electricity' : 'Eletricidade'}</Link></li>
              <li><Link to={getLocalizedPath('/calculadora/imc/')} className="hover:text-white transition-colors">{isEN ? 'BMI Calculator' : 'Calculadora de IMC'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/pressure/')} className="hover:text-white transition-colors">{isEN ? 'Pressure Converter' : 'Conversor de Pressão'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/energy/')} className="hover:text-white transition-colors">{isEN ? 'Energy Converter' : 'Conversor de Energia'}</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-lg font-semibold mb-4 text-white">{isEN ? 'Converters' : 'Conversores'}</p>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><Link to={getLocalizedPath('/conversor/length/')} className="hover:text-white transition-colors">{isEN ? 'Length' : 'Comprimento'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/temperature/')} className="hover:text-white transition-colors">{isEN ? 'Temperature' : 'Temperatura'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/weight/')} className="hover:text-white transition-colors">{isEN ? 'Weight' : 'Peso'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/volume/')} className="hover:text-white transition-colors">{isEN ? 'Volume' : 'Volume'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/area/')} className="hover:text-white transition-colors">{isEN ? 'Area' : 'Área'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/speed/')} className="hover:text-white transition-colors">{isEN ? 'Speed' : 'Velocidade'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/time/')} className="hover:text-white transition-colors">{isEN ? 'Time' : 'Tempo'}</Link></li>
              <li><Link to={getLocalizedPath('/conversor/data_storage/')} className="hover:text-white transition-colors">{isEN ? 'Digital Data' : 'Dados Digitais'}</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-lg font-semibold mb-4 text-white">{isEN ? 'Navigation' : 'Navegação'}</p>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><Link to={getLocalizedPath('/')} className="hover:text-white transition-colors">{isEN ? 'Home Page' : 'Página Inicial'}</Link></li>
              <li><Link to={getLocalizedPath('/recursos/')} className="hover:text-white transition-colors">{isEN ? 'Resources & Articles' : 'Recursos e Artigos'}</Link></li>
              <li><Link to={getLocalizedPath('/sobre/')} className="hover:text-white transition-colors">{isEN ? 'About TudoConversor' : 'Sobre o TudoConversor'}</Link></li>
              <li><Link to={getLocalizedPath('/contato/')} className="hover:text-white transition-colors">{isEN ? 'Contact' : 'Contato'}</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-lg font-semibold mb-4 text-white">{isEN ? 'Legal' : 'Legal'}</p>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><Link to={getLocalizedPath('/privacy-policy/')} className="hover:text-white transition-colors">{isEN ? 'Privacy Policy' : 'Política de Privacidade'}</Link></li>
              <li><Link to={getLocalizedPath('/terms-of-service/')} className="hover:text-white transition-colors">{isEN ? 'Terms of Service' : 'Termos de Serviço'}</Link></li>
              <li><Link to={getLocalizedPath('/data-protection/')} className="hover:text-white transition-colors">{isEN ? 'Data Protection' : 'Proteção de Dados'}</Link></li>
              <li><Link to={getLocalizedPath('/cookies-policy/')} className="hover:text-white transition-colors">{isEN ? 'Cookies Policy' : 'Política de Cookies'}</Link></li>
              <li><Link to={getLocalizedPath('/legal-notice/')} className="hover:text-white transition-colors">{isEN ? 'Legal Notice' : 'Aviso Legal'}</Link></li>
            </ul>
          </div>

        </div>
        
        <div className="mt-12 pt-8 border-t border-white/10 flex justify-center md:justify-start">
          <ShareButtons variant="dark" />
        </div>

        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/60">
          <p>&copy; {new Date().getFullYear()} TudoConversor. {isEN ? 'All rights reserved.' : 'Todos os direitos reservados.'}</p>
          <p>{isEN ? 'Built for maximum precision.' : 'Construído para máxima precisão.'}</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
